import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    BusyPlayer,
    HeatRegistration,
    Match,
    MatchDetail,
    MatchDisplay,
    MatchStatus,
    Table,
} from '@iziping/common';
import { apiClient } from '@iziping/common';
import { ENDPOINTS, QUERY_CACHE } from '@/model/endpoints';
import type { ApiError } from '@iziping/common';
import type { AxiosResponse } from 'axios';

export const useGetGroups = (heatId: number) => {
    return useQuery<MatchDisplay[], ApiError>({
        queryKey: [QUERY_CACHE.GROUPS],
        queryFn: async () => {
            const response = await apiClient.get<MatchDisplay[]>(`${ENDPOINTS.getGroups(heatId)}`);
            return response.data;
        },
        enabled: true,
    });
};

export const useGetBracket = (heatId: number) => {
    return useQuery<MatchDisplay[], ApiError>({
        queryKey: [QUERY_CACHE.BRACKET],
        queryFn: async () => {
            const response = await apiClient.get<MatchDisplay[]>(`${ENDPOINTS.getBracket(heatId)}`);
            return response.data;
        },
        enabled: true,
    });
};

export type MatchesSearchParam = {
    tournamentId: number;
    status: MatchStatus;
};

type useSearchMatchesParam = {
    params: MatchesSearchParam;
};

export const useSearchMatchesInProgress = ({ params }: useSearchMatchesParam) => {
    return useQuery<MatchDetail[], ApiError>({
        queryKey: [QUERY_CACHE.MATCHES_SEARCH_IN_PROGRESS],
        queryFn: async () => {
            const response = await apiClient.get<MatchDetail[]>(`${ENDPOINTS.searchMatchesByStatus}`, {
                params: { ...params },
            });
            return response.data;
        },
        enabled: true,
    });
};

type tournamentParam = {
    tournamentId: number;
};

export const useSearchMatchesPending = ({ params }: useSearchMatchesParam) => {
    return useQuery<MatchDetail[], ApiError>({
        queryKey: [QUERY_CACHE.MATCHES_SEARCH_PENDING],
        queryFn: async () => {
            const response = await apiClient.get<MatchDetail[]>(`${ENDPOINTS.searchMatchesPending}`, {
                params: { ...params },
            });
            return response.data;
        },
        enabled: true,
    });
};

export const useSearchMatchesFinished = ({ tournamentId }: tournamentParam) => {
    return useQuery<MatchDetail[], ApiError>({
        queryKey: [QUERY_CACHE.MATCHES_SEARCH_FINISHED],
        queryFn: async () => {
            const response = await apiClient.get<MatchDetail[]>(`${ENDPOINTS.searchFinishedMatches}`, {
                params: { tournamentId },
            });
            return response.data;
        },
        enabled: true,
    });
};

export const useSearchBusyPlayers = ({ tournamentId }: tournamentParam) => {
    return useQuery<BusyPlayer[], ApiError>({
        queryKey: [QUERY_CACHE.MATCHES_BUSY_PLAYERS],
        queryFn: async () => {
            const response = await apiClient.get<BusyPlayer[]>(`${ENDPOINTS.findBusyPlayers}`, {
                params: { tournamentId },
            });
            return response.data;
        },
        enabled: true,
    });
};

export const useFindAvailableTables = ({ tournamentId }: tournamentParam) => {
    return useQuery<Table[], ApiError>({
        queryKey: [QUERY_CACHE.MATCHES_AVAILABLE_TABLES],
        queryFn: async () => {
            const response = await apiClient.get<Table[]>(`${ENDPOINTS.findAvailableTables}`, {
                params: { tournamentId },
            });
            return response.data;
        },
        enabled: true,
    });
};

interface LaunchMatchParams {
    matchId: number;
    table: Table;
}

export const useLaunchMatch = () => {
    return useMutation<MatchDetail, ApiError, any>({
        mutationKey: [QUERY_CACHE.MATCH_LAUNCH],
        mutationFn: async (params: LaunchMatchParams) => {
            const response = await apiClient.post<MatchDetail>(ENDPOINTS.launchMatch(params.matchId), params.table);
            return response.data;
        },
    });
};

export const useStandByMatch = () => {
    return useMutation<void, ApiError, any>({
        mutationKey: [QUERY_CACHE.MATCH_STANDBY],
        mutationFn: async (params: LaunchMatchParams) => {
            const response = await apiClient.put(ENDPOINTS.standByMatch(params.matchId));
            return response.data;
        },
    });
};

export interface FinishMatchParams {
    matchId: number;
    opponents?: HeatRegistration[];
}

export const useFinishMatch = () => {
    return useMutation<MatchDetail, ApiError, any>({
        mutationKey: [QUERY_CACHE.MATCH_FINISH],
        mutationFn: async (params: FinishMatchParams) => {
            const response = await apiClient.post<MatchDetail>(ENDPOINTS.finishMatch(params.matchId), params.opponents);
            return response.data;
        },
    });
};

export const useModifyMatchResults = () => {
    return useMutation<MatchDetail, ApiError, any>({
        mutationKey: [QUERY_CACHE.MATCH_MODIFY],
        mutationFn: async (params: FinishMatchParams) => {
            const response = await apiClient.put<MatchDetail>(ENDPOINTS.modifyMatch(params.matchId), params.opponents);
            return response.data;
        },
    });
};

interface GetPlayerMatchesParams {
    playerId: string;
    tournamentId: number;
}

export const useSearchPlayerMatches = ({ tournamentId, playerId }: GetPlayerMatchesParams) => {
    return useQuery<MatchDetail[], ApiError>({
        queryKey: [QUERY_CACHE.PLAYER_MATCHES_LIST],
        queryFn: async () => {
            const response = await apiClient.get<MatchDetail[]>(`${ENDPOINTS.findPlayerMatches}`, {
                params: { tournamentId, playerId },
            });
            return response.data;
        },
        enabled: true,
    });
};

export const useDownloadSelectedMatchesToPrint = () => {
    return useMutation<AxiosResponse<Blob>, Error, { tournamentId: number; matchIds: number[] }>({
        mutationFn: async ({ tournamentId, matchIds }) => {
            const response = await apiClient.post(`${ENDPOINTS.downloadSelectedMatchesToPrint}`, matchIds, {
                params: { tournamentId },
                responseType: 'blob',
                headers: {
                    Accept: 'application/pdf',
                },
            });
            return response;
        },
    });
};

export const useDownloadMatchToPrint = () => {
    return useMutation<AxiosResponse<Blob>, Error, { matchId: number }>({
        mutationFn: async ({ matchId }) => {
            const response = await apiClient.get(`${ENDPOINTS.downloadMatchToPrint(matchId)}`, {
                responseType: 'blob', // très important !
                headers: {
                    Accept: 'application/pdf',
                },
            });
            return response;
        },
    });
};

export const useGetMatch = (id: number) => {
    return useQuery<Match, ApiError>({
        queryKey: [QUERY_CACHE.MATCH],
        queryFn: async () => {
            const response = await apiClient.get<Match>(`${ENDPOINTS.getMatch(id)}`);
            return response.data;
        },
        enabled: true,
    });
};

export const useGetMatchDetail = (id: number) => {
    return useQuery<MatchDetail, ApiError>({
        queryKey: [QUERY_CACHE.MATCH, 'detail', id],
        queryFn: async () => {
            const response = await apiClient.get<MatchDetail>(`${ENDPOINTS.getMatchDetail(id)}`);
            return response.data;
        },
        enabled: !!id,
        staleTime: 30_000,
    });
};
